import { useTranslation } from "react-i18next";
import { BookText, Gavel, FileText, Quote, Link2, ShieldCheck, ArrowRight } from "lucide-react";
import { Reveal, RevealStagger } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";

const SOURCE_TYPES = [
  { icon: BookText, key: "law" },
  { icon: Gavel, key: "caselaw" },
  { icon: FileText, key: "docs" },
] as const;

const STEPS = [
  { icon: Quote, key: "cite" },
  { icon: Link2, key: "link" },
  { icon: ShieldCheck, key: "verify" },
] as const;

export function TracciabilitaSection() {
  const { t } = useTranslation();
  return (
    <section id="tracciabilita" className="relative overflow-hidden bg-background py-16 sm:py-24 md:py-28">
      <div className="absolute inset-0 vision-grid-bg opacity-[0.04]" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <EditorialHeading
          index="No. 02"
          eyebrow={t("tracciabilita.eyebrow")}
          title={t("tracciabilita.title")}
          subtitle={t("tracciabilita.subtitle")}
        />

        <RevealStagger className="mt-12 grid gap-4 sm:gap-6 md:grid-cols-3" stagger={0.1} y={28} blur={8}>
          {SOURCE_TYPES.map((s) => (
            <div
              key={s.key}
              className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-500 hover:-translate-y-1 hover:border-accent/40 hover:shadow-xl"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-transform duration-500 group-hover:scale-110">
                  <s.icon className="h-5 w-5" />
                </span>
                <span className="rounded-full border border-border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {t(`tracciabilita.sources.${s.key}.tag`)}
                </span>
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold sm:text-xl">
                {t(`tracciabilita.sources.${s.key}.title`)}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground sm:text-sm">
                {t(`tracciabilita.sources.${s.key}.desc`)}
              </p>
            </div>
          ))}
        </RevealStagger>

        <RevealStagger className="mt-6 grid gap-4 sm:gap-6 md:grid-cols-3 sm:mt-8" stagger={0.1} y={24} blur={6}>
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-start gap-3 rounded-xl border border-border/60 bg-card/50 p-4">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                <s.icon className="h-4 w-4" />
              </span>
              <div>
                <h3 className="font-display text-sm font-semibold">
                  <span className="text-accent">{i + 1}.</span> {t(`tracciabilita.steps.${s.key}.title`)}
                </h3>
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                  {t(`tracciabilita.steps.${s.key}.desc`)}
                </p>
              </div>
            </div>
          ))}
        </RevealStagger>

        <Reveal className="mx-auto mt-10 max-w-3xl text-center" y={20} blur={6}>
          <p className="rounded-xl border border-border/60 bg-muted/30 px-5 py-4 text-[13px] leading-relaxed text-muted-foreground">
            {t("tracciabilita.note")}
          </p>
          <a
            href="#demo"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent transition-colors hover:text-accent/80"
          >
            {t("tracciabilita.cta")} <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
